// Expressions validées par l'utilisateur dans le labo (tools/lab/expressions.json), avec repli sur des
// recettes par défaut tant qu'une émotion n'a pas été validée.
import baked from '../../tools/lab/expressions.json';
import type { Expression } from './Face';

type Validated = Record<string, { channels: Record<string, number> }>;

/** Validations connues : celles de la construction, remplacées par celles du serveur (labo) dès qu'il répond. */
let validated: Validated = baked as Validated;

/**
 * Relit les expressions validées auprès du serveur (`/__lab`, serveurs de dev et de prévisualisation) : une émotion
 * re-validée dans le labo s'applique sans reconstruire. Renvoie vrai si quelque chose a changé. Hébergement statique
 * (pas de serveur de labo) : on garde celles de la construction.
 */
export async function refreshValidated(): Promise<boolean> {
  try {
    const r = await fetch('/__lab', { cache: 'no-store' });
    if (!r.ok || !(r.headers.get('content-type') ?? '').includes('json')) return false;
    const next = await r.json() as Validated;
    if (!next || typeof next !== 'object' || JSON.stringify(next) === JSON.stringify(validated)) return false;
    validated = next;
    return true;
  } catch { return false; }
}

const FALLBACK: Record<string, Expression> = {
  sourire: { 'bu:MouthLeftPullUp': 0.5, 'bu:MouthRightPullUp': 0.5, 'bu:LeftCheekUp': 1.0, 'bu:RightCheekUp': 1.0, 'bu:NasolabialDeepener': 0.6 },
  'grand-sourire': { 'bu:MouthLeftPullUp': 0.7, 'bu:MouthRightPullUp': 0.7, 'bu:UpperLipStretched': 0.6, 'bu:lowerLipDown': 0.25, 'bu:JawDrop': 0.1,
    'bu:LeftCheekUp': 1.5, 'bu:RightCheekUp': 1.5, 'bu:LeftLowerLidUp': 0.9, 'bu:RightLowerLidUp': 0.9, 'bu:NasolabialDeepener': 1.0 },
  rire: { 'bu:MouthLeftPullUp': 0.7, 'bu:MouthRightPullUp': 0.7, 'bu:UpperLipStretched': 0.6, 'bu:lowerLipDown': 0.5, 'bu:JawDrop': 0.45,
    'bu:LeftCheekUp': 1.6, 'bu:RightCheekUp': 1.6, 'bu:LeftLowerLidUp': 1.0, 'bu:RightLowerLidUp': 1.0, 'bu:NasolabialDeepener': 1.1 },
  timide: { 'bu:MouthLeftPullUp': 0.3, 'bu:MouthRightPullUp': 0.3, 'bu:LeftCheekUp': 0.6, 'bu:RightCheekUp': 0.6, 'bu:lowerLipUp': 0.3,
    'bu:LeftInnerBrowUp': 0.4, 'bu:RightInnerBrowUp': 0.4, mouthPressLeft: 0.12, mouthPressRight: 0.12 },
  surprise: { 'bu:JawDrop': 0.25, 'bu:LeftInnerBrowUp': 0.7, 'bu:RightInnerBrowUp': 0.7, 'bu:LeftOuterBrowUp': 0.7, 'bu:RightOuterBrowUp': 0.7,
    eyeWideLeft: 0.4, eyeWideRight: 0.4, mouthFunnel: 0.12 },
  tristesse: { 'bu:MouthLeftPullDown': 0.4, 'bu:MouthRightPullDown': 0.4, 'bu:LeftInnerBrowUp': 0.8, 'bu:RightInnerBrowUp': 0.8, 'bu:lowerLipUp': 0.25 },
  boudeuse: { 'bu:MouthLeftPullDown': 0.3, 'bu:MouthRightPullDown': 0.3, 'bu:lowerLipUp': 0.35, 'bu:LeftBrowDown': 0.3, 'bu:RightBrowDown': 0.3, mouthPucker: 0.2 },
  tendresse: { 'bu:MouthLeftPullUp': 0.3, 'bu:MouthRightPullUp': 0.3, 'bu:LeftCheekUp': 0.7, 'bu:RightCheekUp': 0.7, 'bu:LeftLowerLidUp': 0.5,
    'bu:RightLowerLidUp': 0.5, 'bu:LeftUpperLidClosed': 0.25, 'bu:RightUpperLidClosed': 0.25 },
};

/** Expression nommée (validée si possible), mise à l'échelle. */
export function expr(id: string, scale = 1): Expression {
  const v = validated[id]?.channels ?? FALLBACK[id] ?? {};
  const out: Expression = {};
  for (const [k, w] of Object.entries(v)) if (w) out[k] = w * scale;
  return out;
}

export function isValidated(id: string) {
  return !!validated[id];
}
